import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewChild, inject } from '@angular/core';
import { EventService } from '@app/orders/services/event.service';
import { EventSessionForFilterRequestDto, EventSessionForFilterResponseDto } from '@core/services/apiclient';
import { Observable, BehaviorSubject, map } from 'rxjs';
import { TableFilterAbstractComponent } from '@shared/models/abstact-table-filter.model';
import { EventsDropdownListComponent } from '@shared/components/functional/events-dropdown-list/events-dropdown-list.component';
import { Constants } from '@core/app-constants';
import { DateFnsProviderService } from '@core/services/date-fns-provider.service';

@Component({
  selector: 'app-orders-event-filter',
  templateUrl: './orders-event-filter.component.html',
  styleUrls: ['./orders-event-filter.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrdersEventFilterComponent extends TableFilterAbstractComponent implements OnInit {
  private _eventService = inject(EventService);
  private dateFnsProviderService = inject(DateFnsProviderService);
  private readonly cdr = inject(ChangeDetectorRef);

  @ViewChild(EventsDropdownListComponent) eventsDropdownListComponent: EventsDropdownListComponent;
  public eventsByMonth$ = new BehaviorSubject<EventSessionForFilterResponseDto[]>([]);
  public highlightedDates$: Observable<Date[]>;
  minCharacterRequiredForSearch = Constants.MinCharacterRequiredForSearch;

  ngOnInit(): void {
    this.getResetFilterValue();
    const today = new Date();
    this.getEventsByMonth({ month: today.getMonth(), year: today.getFullYear() });
  }

  public getEventsByMonth({ month, year }) {
    this._eventService.getEventsByRange(new Date(year, month, 1), new Date(year, month + 1, 0)).subscribe({
      next: (events: EventSessionForFilterResponseDto[]) => {
        this.getHighlightedDates();
        this.eventsByMonth$.next(events || []);
        this.cdr.markForCheck();
      },
    });
  }

  private getHighlightedDates() {
    this.highlightedDates$ = this.eventsByMonth$.pipe(
      map((events: EventSessionForFilterResponseDto[]) => {
        return events.map(({ eventSessionDate }) => eventSessionDate);
      }),
    );
  }

  public sessionChanged(sessionId: number): void {
    this.filterResultChange.emit({
      field: this.filter?.field,
      type: this.filter?.type,
      result: sessionId,
      condition: this.filter?.operator,
    });
  }

  public autocompleteEvents = (filter: EventSessionForFilterRequestDto) =>
    this._eventService.autocompleteEventForFilterList(filter);

  public dateFilterEvents = (date: Date) => {
    return this.eventsByMonth$
      .asObservable()
      .pipe(
        map((events: EventSessionForFilterResponseDto[]) =>
          (events || []).filter(({ eventSessionDate }) =>
            this.dateFnsProviderService.isSameDay(eventSessionDate, date),
          ),
        ),
      );
  };

  clearFilter() {
    this.eventsDropdownListComponent.clearInputValue();
  }
}
